// 持卡人邀请注册 PAGE-CHD-AUTH-001
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Link2 } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useToast } from '@/components/Toast';
import { inviteRegister, previewInvite } from '@/api/auth';
import { useLocalePath } from '@/i18n/useLocalePath';
import { mapApiError } from '@/lib/apiErrorMessages';

export default function RegisterInvitePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { localePath: lp } = useLocalePath();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('invite')?.trim() || '', [params]);

  const [merchantName, setMerchantName] = useState('');
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [agreeTos, setAgreeTos] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setPreviewError('缺少邀请参数，请使用商户提供的完整邀请链接');
      return;
    }
    void (async () => {
      const res = await previewInvite(token);
      if (!res.ok) {
        // 未收录码透传后端原文；空 error（纯网络层失败）保留原兜底文案
        setPreviewError(res.error ? t(mapApiError(res.code, res.error)) : '邀请链接无效');
        return;
      }
      if (res.data.status !== 'ACTIVE') {
        setPreviewError(`邀请不可用：${res.data.status}`);
        return;
      }
      setMerchantName(res.data.merchantDisplayName || '商户');
      setPreviewError(null);
    })();
  }, [token, t]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (!email.trim() || !password.trim()) {
      toast.error('请填写邮箱与密码');
      return;
    }
    if (password !== confirm) {
      toast.error('两次密码不一致');
      return;
    }
    if (!agreeTos) {
      toast.error('请勾选同意服务条款后再注册');
      return;
    }
    setSubmitting(true);
    try {
      const res = await inviteRegister({
        inviteToken: token,
        email: email.trim(),
        password,
      });
      if (!res.ok) {
        toast.error(t(mapApiError(res.code, res.error)));
        return;
      }
      toast.success('注册成功，请登录；首次登录需设置支付密码');
      navigate(lp('/login'), {
        replace: true,
        state: { registeredEmail: email.trim(), needVerify: true },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-5 relative overflow-hidden">
      <div className="absolute inset-0 bg-hero-mesh pointer-events-none" />
      <div className="relative w-full max-w-md animate-fade-up">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher variant="full" />
        </div>
        <div className="text-center mb-8">
          <BrandLogo to={lp('/')} variant="dark" size="lg" className="justify-center" />
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-8 shadow-elevated">
          <div className="flex items-center gap-2 text-slate-300 mb-4">
            <Link2 className="w-4 h-4 text-brand-400" />
            <h1 className="text-lg font-semibold text-white">邀请注册</h1>
          </div>
          {previewError ? (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
              {previewError}
              <div className="mt-3">
                <Link to={lp('/login')} className="text-brand-300 underline text-xs">
                  返回登录
                </Link>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-400 mb-5">
                您正通过 <span className="text-white font-medium">{merchantName || '…'}</span> 的邀请注册。
                注册后需登录并验证邮箱，首次登录将引导设置 6 位数字支付密码。
              </p>
              <form onSubmit={(e) => void submit(e)} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">邮箱（登录账号）</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-3 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-brand-400"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">密码</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-3 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-brand-400"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">确认密码</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-brand-400"
                  />
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer text-xs text-slate-400 leading-5">
                  <input
                    type="checkbox"
                    name="agreeTos"
                    checked={agreeTos}
                    onChange={(e) => setAgreeTos(e.target.checked)}
                    className="mt-0.5 rounded border-white/20 bg-white/5"
                  />
                  <span>我已阅读并同意服务条款与隐私政策</span>
                </label>
                <button
                  type="submit"
                  disabled={submitting || !token || !agreeTos}
                  className="w-full min-h-[48px] rounded-xl bg-brand-gradient text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? '提交中…' : '完成注册'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
              <p className="text-center text-xs text-slate-500 mt-5">
                已有账号？{' '}
                <Link to={lp('/login')} className="text-brand-300 hover:underline">
                  去登录
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
